import { Button, Flex, Heading, Popover, Tag, Text } from '@chakra-ui/react';
import { DateTime } from 'luxon';
import { memo, useCallback, useState } from 'react';

import ConditionTextInput from '@components/common/ConditionTextInput';
import DatePicker from '@components/common/date/DatePicker';
import type { SignalType } from '@features/create/CreateSignal';
import { shortMonthFormat } from 'helpers/dates';
import { ITEM_UNITS } from 'helpers/fakeData';
import { CalendarIcon } from 'vectors';

const SIGNAL_ACTION_LABELS: Record<SignalType, string> = {
  demand: 'buy',
};

const HEADING_LABELS: Record<SignalType, (name: string) => string> = {
  demand: (name) => `In need of ${name}`,
};

const ConditionDatePicker = ({
  label,
  selectedDate,
  onDateSelect,
}: {
  label: string;
  selectedDate: DateTime | undefined;
  onDateSelect: (date: DateTime) => void;
}) => {
  return (
    <Flex direction="row" gap={2} alignItems="center">
      <Tag.Root bg="surface.muted" borderColor="border.light" borderWidth="1px" borderRadius="xl">
        <Tag.Label>{label}</Tag.Label>
      </Tag.Root>
      <Popover.Root positioning={{ placement: 'bottom-start' }}>
        <Popover.Trigger asChild>
          <Button
            size="xs"
            variant="subtle"
            borderRadius="lg"
            justifyContent="space-between"
            bg="surface.muted"
            borderColor="border.light"
            borderWidth="1px"
            gap={2}
          >
            <CalendarIcon boxSize={3} color="gray.500" />
            <Text fontSize="xs">
              {selectedDate ? shortMonthFormat(selectedDate) : 'Select date'}
            </Text>
          </Button>
        </Popover.Trigger>
        <Popover.Positioner>
          <Popover.Content p={0} w="fit-content">
            <Flex borderRadius={8} bgColor="surface.background" p={2}>
              <DatePicker
                minDate={DateTime.now().minus({ years: 10 })}
                maxDate={DateTime.now().plus({ years: 10 })}
                selected={selectedDate}
                onDateSelect={onDateSelect}
                dateType="month"
              />
            </Flex>
          </Popover.Content>
        </Popover.Positioner>
      </Popover.Root>
    </Flex>
  );
};

const EditConditions = ({
  signalType,
  commodityName,
}: {
  signalType: SignalType;
  commodityName: string;
}) => {
  const [price, setPrice] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('kg');
  const [selectedDate, setSelectedDate] = useState<DateTime | undefined>(undefined);

  const onPriceChange = useCallback((details: { value: string }) => {
    setPrice(details.value);
  }, []);

  const onQuantityChange = useCallback((details: { value: string }) => {
    setQuantity(details.value);
  }, []);

  const onUnitChange = useCallback((next: string) => {
    setUnit(next);
  }, []);

  const onDateSelect = useCallback((date: DateTime) => {
    setSelectedDate(date);
  }, []);

  return (
    <Flex direction="column" gap={6} p={4}>
      <Heading fontSize="sm" lineHeight={1}>
        {HEADING_LABELS[signalType](commodityName)}
      </Heading>
      <ConditionTextInput
        label="Price"
        value={price}
        onValueChange={onPriceChange}
        formatOptions={{
          style: 'currency',
          currency: 'GHS',
          currencyDisplay: 'code',
        }}
      />
      <ConditionTextInput
        label="Quantity"
        value={quantity}
        onValueChange={onQuantityChange}
        formatOptions={{ maximumFractionDigits: 2 }}
        unit={unit}
        unitOptions={ITEM_UNITS}
        onUnitChange={onUnitChange}
      />
      <ConditionDatePicker label="Date" selectedDate={selectedDate} onDateSelect={onDateSelect} />
      <Flex>
        <Text fontSize="sm" color="text.secondary">
          You intend to {SIGNAL_ACTION_LABELS[signalType]} {quantity !== '' ? quantity : '—'} {unit} of{' '}
          {commodityName} at a target price of {price !== '' ? price : '—'} cedis per unit
          {selectedDate != null ? ` by ${shortMonthFormat(selectedDate)}` : ''}.
        </Text>
      </Flex>
    </Flex>
  );
};

export default memo(EditConditions);
